import { useTranslation } from 'react-i18next';

import {
    type AuditLogFilters,
    auditValueDetail,
    flattenJson,
    jsonLeafFilter,
    useAuditText,
} from '@/domain/auditLog';

import DetailsRow from './DetailsRow';

import styles from './RequestDataList.module.scss';

interface RequestDataListProps {
    metadata: Record<string, unknown>;
    onFilter: (filters: AuditLogFilters) => void;
}

/**
 * Every scalar leaf of the request data as `path  value`. A list of ids shows one row per
 * id under the list's path, and its button finds the entries whose list holds that id.
 * A redacted value reads "hidden" and has no button: the secret was never stored.
 */
export default function RequestDataList({ metadata, onFilter }: RequestDataListProps) {
    const { t } = useTranslation();
    const render = useAuditText();
    const flattened = flattenJson(metadata);

    if (flattened.kind === 'truncated') {
        return (
            <p className={styles.marker}>
                {flattened.length === null
                    ? t('auditLog.summary.tooLarge')
                    : t('auditLog.details.tooLarge', { length: flattened.length })}
            </p>
        );
    }
    if (flattened.leaves.length === 0) {
        return <p className={styles.marker}>{t('auditLog.details.none')}</p>;
    }
    return (
        <>
            <dl className={styles.list}>
                {flattened.leaves.map((leaf, index) => {
                    const filter = jsonLeafFilter(leaf);
                    const detail = auditValueDetail(leaf.value);
                    return (
                        <DetailsRow
                            // A list's elements share its path, so the position tells them apart.
                            key={`${index}:${leaf.path}`}
                            label={leaf.path}
                            isPath
                            value={render(detail.text)}
                            title={detail.title}
                            note={leaf.arrayElement ? t('auditLog.details.listItem') : null}
                            onFilter={filter === null ? undefined : () => onFilter(filter)}
                        />
                    );
                })}
            </dl>
            {flattened.omitted > 0 && (
                <p className={styles.marker}>
                    {t('auditLog.details.omitted', { count: flattened.omitted })}
                </p>
            )}
        </>
    );
}
